import { FC } from 'react'
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import {
  CalendarTodayOutlined,
  HomeOutlined,
  HomeWorkOutlined,
  InsertPhotoOutlined,
  LocationOnOutlined
} from '@mui/icons-material'

import { useOfferList } from 'services/offer/hooks'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import { primaryButtonSx } from 'components/Helpdesk/FormPrimitives'

// Offers list ("Panel Klienta 3.0"): card grid on desktop, single column on mobile.
const OfferList: FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const { data: offerList = [], isFetching, isLoading } = useOfferList()

  if (isFetching || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!offerList.length) {
    return (
      <HelpdeskEmptyState
        Icon={HomeWorkOutlined}
        title={formatMessage({
          id: 'widgetOffer.noActiveOffers',
          defaultMessage: 'Nie masz aktywnych ofert, poproś o nową ofertę'
        })}
      />
    )
  }

  const infoRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: theme.r4r.grey
  } as const

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: '20px'
      }}>
      {offerList.map((offer) => (
        <Box
          key={`offer-${offer.id}`}
          sx={{
            backgroundColor: '#fff',
            boxShadow: theme.r4r.cardShadow,
            display: 'flex',
            flexDirection: 'column'
          }}>
          {offer.flat?.photos[0]?.file ? (
            <Box
              component='img'
              src={offer.flat.photos[0].file}
              alt=''
              sx={{ width: '100%', height: '170px', objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '170px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                backgroundColor: theme.r4r.greyBg,
                color: theme.r4r.grey
              }}>
              <InsertPhotoOutlined sx={{ fontSize: '32px' }} />
              <Typography sx={{ fontSize: '13px' }}>
                {formatMessage({ id: 'offer.noImages', defaultMessage: 'Brak zdjęć' })}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              padding: '18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              flex: 1
            }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: theme.r4r.navy }}>
              {offer.investmentName}
            </Typography>
            <Box sx={infoRowSx}>
              <LocationOnOutlined sx={{ fontSize: '17px' }} />
              <Typography sx={{ fontSize: '13px' }}>{offer.flat.address}</Typography>
            </Box>
            <Box sx={infoRowSx}>
              <HomeOutlined sx={{ fontSize: '17px' }} />
              <Typography sx={{ fontSize: '13px' }}>{offer.flatType}</Typography>
            </Box>
            <Box sx={infoRowSx}>
              <CalendarTodayOutlined sx={{ fontSize: '17px' }} />
              <Typography sx={{ fontSize: '13px' }}>
                {dayjs(offer.availableOn).format('DD/MM/YYYY')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                mt: 'auto',
                pt: '10px',
                borderTop: `1px solid ${theme.r4r.greyLight}`
              }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy }}>
                  {+offer.price} zł
                </Typography>
                <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                  {formatMessage({ id: 'offer.perMonth', defaultMessage: '/mies.' })}
                </Typography>
              </Box>
              <Button
                onClick={() => navigate(`/offer/${offer.id}`)}
                sx={{ ...primaryButtonSx, padding: '8px 18px' }}>
                {formatMessage({ id: 'offer.seeTheOffer', defaultMessage: 'Obejrzyj' })}
              </Button>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default OfferList
